import { useCallback, useEffect, useState, type FormEvent } from 'react'
import type {
  CampaignRoll,
  InitiativeRow,
  InitiativeState,
  MasterCatalogue,
  MasterCommand,
  MonsterListEntry,
  MonsterRow,
  PartyMember,
} from '@rollcall/contracts'
import type { TFunction } from 'i18next'
import type { Api } from '../api.js'
import { useT } from './i18n-context.js'
import { SectionHead } from './hint.js'
import { contentName, featureName, resourceName } from './labels.js'
import { reasonLabel } from './rolls.js'

/**
 * Панель мастера (D3, D10).
 *
 * Один макет на телефон и на широкий экран: карточки партии лежат
 * в сетке, которая на узком экране становится столбцом сама.
 * Отдельной мобильной вёрстки нет и не будет — два макета расходятся
 * в первый же день, когда правку внесут в один.
 *
 * Ни одного расчёта здесь нет. Мастер жмёт «−7 хитов», сервер считает
 * и возвращает пересчитанную строку; компонент показывает то, что
 * пришло. Посчитанное здесь разошлось бы с сервером в тот же день
 * (CLAUDE.md, п. 5).
 */

function Refusal({ error }: { error: unknown }) {
  const t = useT()
  if (error === null || error === undefined) return null
  const text = error instanceof Error ? error.message : t('master.failed')
  return (
    <p className="app-problems" role="alert">
      {text}
    </p>
  )
}

/**
 * Трекер опыта: сколько есть и сколько до следующего уровня.
 *
 * Стоит под именем персонажа и там же говорит, что порог взят.
 * Само повышение здесь не делается: выборы уровня — дело игрока
 * (D31), и нажать за него нельзя.
 */
export function ExperienceBar({
  xp,
  compact = false,
}: {
  readonly xp: PartyMember['xp']
  readonly compact?: boolean
}) {
  const t = useT()
  return (
    <p className="app-value-note" data-testid="xp">
      {t('master.xp.total', { total: xp.total })}
      {xp.nextAt !== null &&
        ` · ${t('master.xp.next', { level: xp.nextLevel, at: xp.nextAt })}`}
      {xp.levelUpAvailable && (
        <strong className="app-auth-notice" data-testid="level-up-ready">
          {compact ? ' ★ ' : ' '}
          {t('master.xp.ready')}
        </strong>
      )}
    </p>
  )
}

/** Команды над одним персонажем: хиты, предметы, состояния, опыт. */
function CommandPanel({
  member,
  catalogue,
  busy,
  onCommand,
}: {
  readonly member: PartyMember
  readonly catalogue: MasterCatalogue | null
  readonly busy: boolean
  readonly onCommand: (command: MasterCommand) => void
}) {
  const t = useT()
  const [amount, setAmount] = useState(1)
  const [item, setItem] = useState('')
  const [quantity, setQuantity] = useState(1)
  const [condition, setCondition] = useState('')
  const [xp, setXp] = useState(50)

  const chosen = catalogue?.conditions.find((row) => row.id === condition) ?? null

  return (
    <div className="app-master-commands">
      <label className="app-field app-field-inline">
        <span className="app-field-label">{t('master.hp.amount')}</span>
        <input
          type="number"
          min={0}
          value={amount}
          onChange={(event) => {
            setAmount(Number(event.target.value))
          }}
        />
      </label>
      <div className="app-actions">
        {(['damage', 'heal', 'temp'] as const).map((kind) => (
          <button
            key={kind}
            type="button"
            className="app-button app-button-quiet"
            disabled={busy}
            onClick={() => {
              onCommand({ kind, characterId: member.characterId, amount })
            }}
          >
            {t(`master.hp.${kind}`)}
          </button>
        ))}
      </div>

      <label className="app-field app-field-inline">
        <span className="app-field-label">{t('master.item.title')}</span>
        <select
          value={item}
          onChange={(event) => {
            setItem(event.target.value)
          }}
        >
          <option value="">{t('master.item.choose')}</option>
          {(catalogue?.items ?? []).map((row) => (
            <option key={row.id} value={row.id}>
              {contentName(t, row.id, row.name)}
            </option>
          ))}
        </select>
      </label>
      <label className="app-field app-field-inline">
        <span className="app-field-label">{t('master.item.quantity')}</span>
        <input
          type="number"
          min={1}
          value={quantity}
          onChange={(event) => {
            setQuantity(Number(event.target.value))
          }}
        />
      </label>
      <div className="app-actions">
        <button
          type="button"
          className="app-button app-button-quiet"
          disabled={busy || item === ''}
          onClick={() => {
            onCommand({
              kind: 'give',
              characterId: member.characterId,
              item,
              quantity,
            })
          }}
        >
          {t('master.item.give')}
        </button>
        <button
          type="button"
          className="app-button app-button-quiet"
          disabled={busy || item === ''}
          onClick={() => {
            onCommand({
              kind: 'take',
              characterId: member.characterId,
              item,
              quantity,
            })
          }}
        >
          {t('master.item.take')}
        </button>
      </div>

      <label className="app-field app-field-inline">
        <span className="app-field-label">{t('master.condition.title')}</span>
        <select
          value={condition}
          onChange={(event) => {
            setCondition(event.target.value)
          }}
        >
          <option value="">{t('master.condition.choose')}</option>
          {(catalogue?.conditions ?? []).map((row) => (
            <option key={row.id} value={row.id}>
              {contentName(t, row.id, row.name)}
            </option>
          ))}
        </select>
      </label>
      <div className="app-actions">
        <button
          type="button"
          className="app-button app-button-quiet"
          disabled={busy || condition === ''}
          onClick={() => {
            onCommand({
              kind: 'condition',
              characterId: member.characterId,
              condition,
              // Уровень накопительного состояния считает сервер:
              // «ещё одно истощение» — это то, что нажал мастер,
              // а какой это по счёту уровень, знает журнал.
              level: null,
            })
          }}
        >
          {chosen?.cumulative === true
            ? t('master.condition.raise')
            : t('master.condition.apply')}
        </button>
        <button
          type="button"
          className="app-button app-button-quiet"
          disabled={busy || condition === ''}
          onClick={() => {
            onCommand({
              kind: 'uncondition',
              characterId: member.characterId,
              condition,
            })
          }}
        >
          {t('master.condition.remove')}
        </button>
      </div>

      <label className="app-field app-field-inline">
        <span className="app-field-label">{t('master.xp.award')}</span>
        <input
          type="number"
          value={xp}
          onChange={(event) => {
            setXp(Number(event.target.value))
          }}
        />
      </label>
      <div className="app-actions">
        <button
          type="button"
          className="app-button app-button-quiet"
          disabled={busy}
          onClick={() => {
            onCommand({ kind: 'xp', characterId: member.characterId, amount: xp })
          }}
        >
          {t('master.xp.give')}
        </button>
      </div>
    </div>
  )
}

/** Карточка персонажа в сводке: то, на что смотрят за столом. */
function MemberCard({
  member,
  catalogue,
  busy,
  onCommand,
}: {
  readonly member: PartyMember
  readonly catalogue: MasterCatalogue | null
  readonly busy: boolean
  readonly onCommand: (command: MasterCommand) => void
}) {
  const t = useT()
  const [open, setOpen] = useState(false)

  return (
    <li className="app-card app-party-card" data-testid={`party-${member.characterId}`}>
      <h4>{member.name}</h4>
      <p className="app-value-note">
        {[member.species, member.characterClass]
          .filter((ref) => ref !== null)
          .map((ref) => contentName(t, ref.id, ref.name))
          .join(' · ')}
        {' · '}
        {t('master.level', { level: member.level })}
        {' · '}
        {member.seatLabel}
      </p>
      <p className="app-party-numbers">
        <span data-testid={`hp-${member.characterId}`}>
          {t('master.hp.title')} {member.hp.current}/{member.hp.max}
          {member.hp.temp > 0 && ` (+${member.hp.temp})`}
        </span>
        <span>
          {t('master.ac')} {member.ac ?? '—'}
        </span>
        <span>
          {t('master.passive')} {member.passivePerception ?? '—'}
        </span>
      </p>
      {/* Счётчики ресурсов (D56): за столом спрашивают «ярости
          остались?», и открывать ради этого чужой лист незачем.
          Мастер меняет их так же, как хиты. */}
      {member.resources.length > 0 && (
        <ul
          className="app-party-resources"
          data-testid={`resources-${member.characterId}`}
        >
          {member.resources.map((resource) => (
            <li key={resource.id}>
              <span>
                {resourceName(t, resource)} {resource.current}/{resource.max}
              </span>
              <button
                type="button"
                className="app-button app-button-quiet"
                disabled={busy || resource.current === 0}
                onClick={() => {
                  onCommand({
                    kind: 'resource',
                    characterId: member.characterId,
                    resource: resource.id,
                    amount: 1,
                  })
                }}
                data-testid={`resource-spend-${member.characterId}-${resource.id}`}
              >
                {t('resources.spend')}
              </button>
              <button
                type="button"
                className="app-button app-button-quiet"
                disabled={busy || resource.current >= resource.max}
                onClick={() => {
                  onCommand({
                    kind: 'resource',
                    characterId: member.characterId,
                    resource: resource.id,
                    amount: -1,
                  })
                }}
                data-testid={`resource-restore-${member.characterId}-${resource.id}`}
              >
                {t('resources.restore')}
              </button>
            </li>
          ))}
        </ul>
      )}

      <ExperienceBar xp={member.xp} compact />
      <p className="app-value-note" data-testid={`conditions-${member.characterId}`}>
        {member.conditions.length === 0
          ? t('master.condition.none')
          : member.conditions
              .map(
                (item) =>
                  contentName(t, item.id, item.name) +
                  (item.level === null ? '' : ` ${item.level}`),
              )
              .join(', ')}
      </p>

      <div className="app-actions">
        <button
          type="button"
          className="app-button app-button-quiet"
          onClick={() => {
            setOpen(!open)
          }}
        >
          {open ? t('master.hide') : t('master.commands')}
        </button>
      </div>

      {open && (
        <CommandPanel
          member={member}
          catalogue={catalogue}
          busy={busy}
          onCommand={onCommand}
        />
      )}
    </li>
  )
}

/**
 * Сводка партии: все персонажи стола сразу.
 *
 * Один и тот же список на телефоне и на широком экране: сетка
 * складывается в столбец сама (D10).
 */
export function PartyPanel({
  api,
  campaignId,
  party,
  onChanged,
}: {
  readonly api: Api
  readonly campaignId: string
  readonly party: readonly PartyMember[]
  readonly onChanged: () => void
}) {
  const t = useT()
  const [catalogue, setCatalogue] = useState<MasterCatalogue | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  useEffect(() => {
    void api.masterCatalogue(campaignId).then(setCatalogue).catch(setError)
  }, [api, campaignId])

  const send = (command: MasterCommand) => {
    setBusy(true)
    setError(null)
    void api
      .command(campaignId, command)
      .then(() => {
        onChanged()
      })
      .catch((cause: unknown) => {
        setError(cause)
      })
      .finally(() => {
        setBusy(false)
      })
  }

  const rest = (kind: 'short' | 'long') => {
    setBusy(true)
    setError(null)
    void api
      .declareRest(campaignId, kind)
      .then(() => {
        onChanged()
      })
      .catch((cause: unknown) => {
        setError(cause)
      })
      .finally(() => {
        setBusy(false)
      })
  }

  return (
    <section>
      {/* Как устроен отдых — за знаком вопроса: объяснение нужно
          один раз, а место занимало каждый раз (D52). */}
      <SectionHead title={t('master.party')} hints={[t('master.rest.hint')]} />
      <Refusal error={error} />

      <div className="app-actions">
        <button
          type="button"
          className="app-button app-button-quiet"
          disabled={busy}
          onClick={() => {
            rest('short')
          }}
        >
          {t('master.rest.short')}
        </button>
        <button
          type="button"
          className="app-button app-button-quiet"
          disabled={busy}
          onClick={() => {
            rest('long')
          }}
        >
          {t('master.rest.long')}
        </button>
      </div>

      {party.length === 0 ? (
        <p>{t('master.empty')}</p>
      ) : (
        <ul className="app-list app-party">
          {party.map((member) => (
            <MemberCard
              key={member.characterId}
              member={member}
              catalogue={catalogue}
              busy={busy}
              onCommand={send}
            />
          ))}
        </ul>
      )}
    </section>
  )
}

/**
 * Экран боя по инициативе (D10).
 *
 * Порядок приходит с сервера уже упорядоченным: сортировать его
 * здесь значило бы завести второе место, где живёт очередь, —
 * и получить у мастера и у игрока разные ответы на вопрос «чей ход».
 */
/**
 * Подпись строки очереди.
 *
 * У монстра к имени добавлен номер: «Волк 1», «Волк 2». Номер нужен
 * ровно затем, зачем он нужен за столом, — чтобы мастер мог сказать,
 * по которому из трёх волков пришёлся удар. Имя переводится по ключу
 * записи (D11, D32), английское остаётся запасным вариантом.
 */
function rowName(row: InitiativeRow, t: TFunction): string {
  if (row.monster === null) return row.label
  return `${contentName(t, row.monster.id, row.label)} ${row.monster.ordinal}`
}

/**
 * Монстр в строке очереди: хиты, КД, состояния и атаки.
 *
 * Числа приходят с сервера пересчитанными — здесь не считается
 * ничего, даже вычитание урона из хитов (CLAUDE.md, п. 5).
 */
function MonsterLine({
  api,
  campaignId,
  rowId,
  monster,
  master,
  busy,
  guard,
}: {
  readonly api: Api
  readonly campaignId: string
  readonly rowId: string
  readonly monster: MonsterRow
  readonly master: boolean
  readonly busy: boolean
  readonly guard: (work: Promise<unknown>) => void
}) {
  const t = useT()
  const [amount, setAmount] = useState('')
  const [hidden, setHidden] = useState(false)

  const damage = (kind: 'damage' | 'heal') => {
    const value = Number(amount)
    if (!Number.isInteger(value) || value <= 0) return
    guard(
      api.monsterHp(campaignId, rowId, kind, value).then(() => {
        setAmount('')
      }),
    )
  }

  return (
    <div className="app-monster" data-testid={`monster-${rowId}`}>
      <p className="app-value-note">
        <span data-testid={`monster-hp-${rowId}`}>
          {t('master.monsters.hp', {
            current: monster.hp.current,
            max: monster.hp.max,
          })}
        </span>{' '}
        · {t('master.monsters.ac', { ac: monster.ac })}
        {monster.hp.current === 0 && ` · ${t('master.monsters.down')}`}
      </p>

      {monster.conditions.length > 0 && (
        <p className="app-value-note" data-testid={`monster-conditions-${rowId}`}>
          {monster.conditions.map((condition) => contentName(t, condition)).join(', ')}
        </p>
      )}

      {master && (
        <>
          <div className="app-actions">
            <label className="app-field app-field-inline">
              <span className="app-field-label">{t('master.monsters.amount')}</span>
              <input
                inputMode="numeric"
                value={amount}
                data-testid={`monster-amount-${rowId}`}
                onChange={(event) => {
                  setAmount(event.target.value)
                }}
              />
            </label>
            <button
              type="button"
              className="app-button app-button-quiet"
              disabled={busy}
              data-testid={`monster-damage-${rowId}`}
              onClick={() => {
                damage('damage')
              }}
            >
              {t('master.monsters.damage')}
            </button>
            <button
              type="button"
              className="app-button app-button-quiet"
              disabled={busy}
              data-testid={`monster-heal-${rowId}`}
              onClick={() => {
                damage('heal')
              }}
            >
              {t('master.monsters.heal')}
            </button>
          </div>

          {monster.attacks.length > 0 && (
            <div className="app-actions">
              <label className="app-check">
                <input
                  type="checkbox"
                  checked={hidden}
                  data-testid={`monster-hidden-${rowId}`}
                  onChange={(event) => {
                    setHidden(event.target.checked)
                  }}
                />
                <span>{t('master.monsters.hidden')}</span>
              </label>
              {monster.attacks.map((attack) => (
                <button
                  key={attack.key}
                  type="button"
                  className="app-button app-button-quiet"
                  disabled={busy}
                  data-testid={`monster-attack-${rowId}-${attack.key}`}
                  onClick={() => {
                    guard(api.rollMonsterAttack(campaignId, rowId, attack.key, hidden))
                  }}
                >
                  {t('master.monsters.attack', {
                    name: featureName(t, attack.key, attack.name),
                    bonus: attack.bonus >= 0 ? `+${attack.bonus}` : `${attack.bonus}`,
                    damage: attack.damage
                      .map((item) => item.dice ?? String(item.average))
                      .join(' + '),
                  })}
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}

/**
 * Справочник монстров (D50).
 *
 * Мастер выбирает запись, а не печатает имя: напечатанное имя
 * не даёт ни хитов, ни КД, ни атак, и бросать за такую строку нечем.
 * Список приходит с сервера отсортированным по опасности; поиск
 * идёт по русскому названию, потому что в списке стоит оно.
 */
function MonsterPicker({
  api,
  campaignId,
  busy,
  guard,
}: {
  readonly api: Api
  readonly campaignId: string
  readonly busy: boolean
  readonly guard: (work: Promise<unknown>) => void
}) {
  const t = useT()
  const [monsters, setMonsters] = useState<MonsterListEntry[]>([])
  const [search, setSearch] = useState('')
  const [chosen, setChosen] = useState('')
  const [count, setCount] = useState('1')

  useEffect(() => {
    void api
      .monsters(campaignId)
      .then(setMonsters)
      .catch(() => {
        setMonsters([])
      })
  }, [api, campaignId])

  const needle = search.trim().toLocaleLowerCase('ru')
  const found = monsters.filter(
    (monster) =>
      needle === '' ||
      contentName(t, monster.id, monster.name).toLocaleLowerCase('ru').includes(needle),
  )

  const add = (event: FormEvent) => {
    event.preventDefault()
    if (chosen === '') return
    const times = Number(count)
    if (!Number.isInteger(times) || times <= 0) return
    guard(api.addMonster(campaignId, chosen, times))
  }

  return (
    <form className="app-combat-add-form" onSubmit={add} data-testid="monster-picker">
      <label className="app-field">
        <span className="app-field-label">{t('master.monsters.search')}</span>
        <input
          value={search}
          data-testid="monster-search"
          onChange={(event) => {
            setSearch(event.target.value)
            setChosen('')
          }}
        />
      </label>
      <label className="app-field">
        <span className="app-field-label">{t('master.monsters.pick')}</span>
        <select
          value={chosen}
          data-testid="monster-select"
          onChange={(event) => {
            setChosen(event.target.value)
          }}
        >
          <option value="">{t('master.monsters.none')}</option>
          {found.slice(0, 60).map((monster) => (
            <option key={monster.id} value={monster.id}>
              {t('master.monsters.option', {
                name: contentName(t, monster.id, monster.name),
                challenge: monster.challenge,
                count: monster.hp,
                ac: monster.ac,
              })}
            </option>
          ))}
        </select>
      </label>
      <label className="app-field">
        <span className="app-field-label">{t('master.monsters.count')}</span>
        <input
          inputMode="numeric"
          value={count}
          data-testid="monster-count"
          onChange={(event) => {
            setCount(event.target.value)
          }}
        />
      </label>
      <button
        type="submit"
        className="app-button"
        disabled={busy || chosen === ''}
        data-testid="monster-add"
      >
        {t('master.monsters.add')}
      </button>
    </form>
  )
}

export function InitiativePanel({
  api,
  campaignId,
  initiative,
  master,
  onChanged,
}: {
  readonly api: Api
  readonly campaignId: string
  readonly initiative: InitiativeState | null
  readonly master: boolean
  readonly onChanged: () => void
}) {
  const t = useT()
  const [label, setLabel] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  const guard = (work: Promise<unknown>) => {
    setBusy(true)
    setError(null)
    void work
      .then(() => {
        onChanged()
      })
      .catch((cause: unknown) => {
        setError(cause)
      })
      .finally(() => {
        setBusy(false)
      })
  }

  const add = (event: FormEvent) => {
    event.preventDefault()
    guard(
      api.addInitiativeRow(campaignId, label, null).then(() => {
        setLabel('')
      }),
    )
  }

  const rows = initiative?.rows ?? []
  const fighting = (initiative?.round ?? 0) > 0

  return (
    <section>
      <SectionHead title={t('master.combat.title')} />
      <Refusal error={error} />

      <p className="app-value-note" data-testid="combat-round">
        {fighting
          ? t('master.combat.round', { round: initiative?.round })
          : t('master.combat.idle')}
      </p>

      {master && (
        <div className="app-actions">
          <button
            type="button"
            className="app-button"
            disabled={busy}
            onClick={() => {
              guard(api.startCombat(campaignId, true))
            }}
          >
            {t('master.combat.startRolled')}
          </button>
          <button
            type="button"
            className="app-button app-button-quiet"
            disabled={busy}
            onClick={() => {
              guard(api.startCombat(campaignId, false))
            }}
          >
            {t('master.combat.startEmpty')}
          </button>
          {fighting && (
            <>
              <button
                type="button"
                className="app-button"
                disabled={busy}
                onClick={() => {
                  guard(api.nextTurn(campaignId))
                }}
              >
                {t('master.combat.next')}
              </button>
              <button
                type="button"
                className="app-button app-button-quiet"
                disabled={busy}
                onClick={() => {
                  guard(api.endCombat(campaignId))
                }}
              >
                {t('master.combat.end')}
              </button>
            </>
          )}
        </div>
      )}

      {rows.length === 0 ? (
        <p>{t('master.combat.empty')}</p>
      ) : (
        <ol className="app-list app-initiative">
          {rows.map((row, index) => (
            <li
              key={row.id}
              className="app-card"
              data-testid={`turn-${index}`}
              data-active={fighting && index === initiative?.turn ? 'yes' : 'no'}
            >
              <p>
                <strong>{row.value ?? '—'}</strong> · {rowName(row, t)}
                {fighting &&
                  index === initiative?.turn &&
                  ` · ${t('master.combat.now')}`}
              </p>
              {row.monster !== null && (
                <MonsterLine
                  api={api}
                  campaignId={campaignId}
                  rowId={row.id}
                  monster={row.monster}
                  master={master}
                  busy={busy}
                  guard={guard}
                />
              )}
              <div className="app-actions">
                <button
                  type="button"
                  className="app-button app-button-quiet"
                  disabled={busy}
                  onClick={() => {
                    guard(api.rollInitiative(campaignId, row.id))
                  }}
                >
                  {t('master.combat.roll')}
                </button>
                {master && (
                  <button
                    type="button"
                    className="app-button app-button-quiet"
                    disabled={busy}
                    onClick={() => {
                      guard(api.removeInitiativeRow(campaignId, row.id))
                    }}
                  >
                    {t('master.combat.remove')}
                  </button>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}

      {master && (
        /* Две формы читаются как одна: у них общая сетка колонок,
           и обе кнопки стоят в последней. До этого каждая форма
           переносилась сама по себе, и поля вставали ступеньками —
           «Существо» начиналось не там, где «Поиск монстра»,
           а кнопка висела ниже соседней без всякой причины.

           Формы при этом остаются двумя: у них разные действия
           и разная проверка. Одна на обе означала бы `if` внутри
           обработчика вместо двух прямых. */
        <div className="app-combat-add">
          <MonsterPicker api={api} campaignId={campaignId} busy={busy} guard={guard} />
          <form className="app-combat-add-form" onSubmit={add}>
            <label className="app-field app-combat-add-wide">
              <span className="app-field-label">{t('master.combat.creature')}</span>
              <input
                required
                value={label}
                onChange={(event) => {
                  setLabel(event.target.value)
                }}
              />
            </label>
            <button
              type="submit"
              className="app-button app-button-quiet"
              disabled={busy}
            >
              {t('master.combat.add')}
            </button>
          </form>
        </div>
      )}
    </section>
  )
}

/**
 * Общий журнал бросков (D12).
 *
 * Скрытый бросок мастера виден отметкой, а не числом: за столом
 * должно быть видно, что мастер бросил, но не что выпало.
 */
export function RollFeed({ rolls }: { readonly rolls: readonly CampaignRoll[] }) {
  const t = useT()
  return (
    <section>
      <SectionHead title={t('master.rolls.title')} />
      {rolls.length === 0 ? (
        <p>{t('master.rolls.empty')}</p>
      ) : (
        <ul className="app-list app-rolls" data-testid="roll-feed">
          {rolls.map((roll) => (
            <li key={roll.id} className="app-card">
              <p>
                <strong>{roll.who}</strong> · {reasonLabel(t, roll.reason)} ·{' '}
                {roll.outcome === null ? (
                  <span data-testid={`hidden-${roll.id}`}>
                    {t('master.rolls.hidden')}
                  </span>
                ) : (
                  <span data-testid={`total-${roll.id}`}>{roll.outcome.total}</span>
                )}
                {roll.hidden &&
                  roll.outcome !== null &&
                  ` · ${t('master.rolls.secret')}`}
              </p>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

/**
 * Общее состояние стола: очередь боя и журнал бросков.
 *
 * Читают его все участники, а не только мастер: порядок ходов
 * и броски за столом — общее знание (D10, D12). Сводка партии сюда
 * не входит — её сервер отдаёт мастеру и только ему.
 */
export function useTableState(api: Api, campaignId: string | null) {
  const [initiative, setInitiative] = useState<InitiativeState | null>(null)
  const [rolls, setRolls] = useState<CampaignRoll[]>([])

  const reload = useCallback(() => {
    if (campaignId === null) return
    void api
      .initiative(campaignId)
      .then(setInitiative)
      .catch(() => {
        setInitiative(null)
      })
    void api
      .campaignRolls(campaignId)
      .then(setRolls)
      .catch(() => {
        setRolls([])
      })
  }, [api, campaignId])

  useEffect(reload, [reload])

  return { initiative, rolls, reload }
}

/** То же плюс сводка партии: её читает мастер. */
export function useMasterState(api: Api, campaignId: string) {
  const table = useTableState(api, campaignId)
  const [party, setParty] = useState<PartyMember[]>([])
  const reloadTable = table.reload

  const reload = useCallback(() => {
    reloadTable()
    void api
      .party(campaignId)
      .then(setParty)
      .catch(() => {
        setParty([])
      })
  }, [api, campaignId, reloadTable])

  useEffect(reload, [reload])

  return { party, initiative: table.initiative, rolls: table.rolls, reload }
}
